"use client"

import { useEffect, useMemo, useRef, useState, useCallback } from "react"
import { Dialog, DialogContent, DialogTitle, DialogClose } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Slider } from "@/components/ui/slider"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import {
  Play, Pause, Square, Volume2, VolumeX,
  Zap, Monitor, Music, FileText, ExternalLink, Settings2,
  Minimize2, X, Edit2, Loader2, ChevronLeft, ChevronRight, LayoutGrid,
  Repeat, Repeat1, StopCircle, Download,
} from "@/lib/icons"
import { toast } from "sonner"
import type { UserScript } from "@/lib/script-store"
import { useDeviceStore } from "@/lib/device-store"
import { ScriptPlaybackEngine, isNoDeviceError, type PlaybackState } from "@/lib/script-playback-engine"
import { startBackground } from "@/lib/background-playback"
import { useSettingsStore } from "@/lib/settings-store"
import { useGlobalPlaybackStore } from "@/lib/global-playback-store"
import { useScriptPlaybackStore } from "@/lib/script-playback-store"
import { ScriptHeatmap } from "@/components/script-heatmap"
import { QuickConnectDeviceModal } from "@/components/quick-connect-device-modal"
import { getImageUrl } from "@/lib/image-utils"
import { normalizeOrderMediaItems, inferOrderMediaType } from "@/lib/order-media"
import { downloadContentItems, extFromUrl, type DownloadItem } from "@/lib/content-download"
import { useAuthStore } from "@/lib/auth-store"
import { fetchSeenState, markSeen } from "@/lib/client/seen-state"
import { useTranslations } from "next-intl"

interface ScriptPlayerCoreProps {
  script: UserScript | null
  open: boolean
  onClose: () => void
  onEdit?: (id: string) => void
  /** True when this is a pre-approval delivery preview (commission/bounty review). */
  previewOnly?: boolean
  /** Playback/seek is clamped to this point (ms) when previewOnly or trialMode is true. */
  previewCutoffMs?: number
  /**
   * True for a marketplace "try before you buy" trial. Like previewOnly, clamps playback at
   * previewCutoffMs, but differs in ways specific to a public unpurchased trial rather than a
   * buyer reviewing their own paid delivery: audio is forced off (no unmute — the video itself
   * isn't the protected asset here, licensing/content concerns are), video is NOT blurred (the
   * whole point is letting a shopper judge sync quality), download is unavailable, and the
   * locked-zone copy says "purchase" instead of "approve".
   */
  trialMode?: boolean
  /** Fires once, the first time playback naturally reaches previewCutoffMs during trialMode. */
  onCutoffReached?: () => void
  /**
   * Hide the top title/badges/keep-playing/edit/download/close row. Used when a host component
   * embeds the player directly inside its own dialog and already shows its own title/chrome
   * around it (e.g. the marketplace detail modal's inline trial). Default true.
   */
  showHeader?: boolean
  /**
   * Start playback automatically once mounted, instead of waiting for a tap on the play button.
   * Used when the host already captured the "play" intent itself before this component even
   * existed (e.g. the marketplace's own "Try" button, clicked before the trial content loads).
   */
  autoPlay?: boolean
  /**
   * When true, pauses playback from outside; when switched back to false, resumes.
   * Used by the marketplace detail modal's Try/Pause button to control the embedded trial player.
   */
  externalPaused?: boolean
}

export type ScriptPlayerModalProps = ScriptPlayerCoreProps

// ─── Preview Watermark ────────────────────────────────────────────────────────

function PreviewWatermark() {
  // Dense enough to make clean screen-recording pointless (the point is to discourage
  // capturing this preview for redistribution before the order is paid for/approved),
  // while staying light enough that the reviewer can still judge sync timing. z-10 is
  // deliberately below the play button / transport controls (z-20) — pointer-events-none
  // already lets clicks through, but it was still visually sitting on top of them.
  const pattern = `<svg xmlns="http://www.w3.org/2000/svg" width="110" height="44"><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" transform="rotate(-20,55,22)" font-family="system-ui" font-size="12" font-weight="700" letter-spacing="2" fill="white" opacity="0.95">PREVIEW · GOONRU</text></svg>`
  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none z-10"
      style={{
        backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(pattern)}")`,
        backgroundRepeat: "repeat",
        opacity: 0.55,
      }}
    />
  )
}

// ─── Locked-zone overlay (visual cue over scrubber/heatmap past the preview cutoff) ──

function LockedZoneOverlay({ lockedFromPercent }: { lockedFromPercent: number }) {
  if (lockedFromPercent >= 100) return null
  return (
    <div
      aria-hidden="true"
      className="absolute inset-y-0 right-0 pointer-events-none bg-background/70"
      style={{
        left: `${lockedFromPercent}%`,
        backgroundImage:
          "repeating-linear-gradient(45deg, rgba(120,120,130,0.35) 0px, rgba(120,120,130,0.35) 4px, transparent 4px, transparent 9px)",
      }}
    />
  )
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isVideoUrl(url: string): boolean {
  return /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(url)
}

function isImageUrl(url: string): boolean {
  return /\.(jpe?g|png|gif|webp|avif|bmp)(\?.*)?$/i.test(url)
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, "0")}`
}

// ─── Waveform Canvas ─────────────────────────────────────────────────────────

function HeatmapCanvas({ script, currentTime }: { script: UserScript; currentTime: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const actions = script.funscript.actions

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || actions.length === 0) return
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const { width, height } = canvas
    ctx.clearRect(0, 0, width, height)

    const totalDuration = actions[actions.length - 1].at || 1
    const playProgress = (currentTime * 1000) / totalDuration

    ctx.fillStyle = "rgba(0,0,0,0.3)"
    ctx.fillRect(0, 0, width, height)
    ctx.fillStyle = "rgba(147,51,234,0.08)"
    ctx.fillRect(0, 0, width * playProgress, height)

    ctx.beginPath()
    ctx.strokeStyle = "rgba(147,51,234,0.8)"
    ctx.lineWidth = 1.5
    for (let i = 0; i < actions.length - 1; i++) {
      const a = actions[i]
      const b = actions[i + 1]
      const x1 = (a.at / totalDuration) * width
      const y1 = height - (a.pos / 100) * height
      const x2 = (b.at / totalDuration) * width
      const y2 = height - (b.pos / 100) * height
      if (i === 0) ctx.moveTo(x1, y1)
      ctx.lineTo(x2, y2)
    }
    ctx.stroke()

    const px = playProgress * width
    ctx.beginPath()
    ctx.strokeStyle = "rgba(255,255,255,0.9)"
    ctx.lineWidth = 2
    ctx.moveTo(px, 0)
    ctx.lineTo(px, height)
    ctx.stroke()
  }, [actions, currentTime])

  return <canvas ref={canvasRef} width={800} height={120} className="w-full h-full" />
}

// ─── Player core (no Dialog wrapper — embeddable directly, or wrapped by ScriptPlayerModal) ────

export function ScriptPlayerCore({ script, open, onClose, onEdit, previewOnly = false, previewCutoffMs, trialMode = false, onCutoffReached, showHeader = true, autoPlay = false, externalPaused }: ScriptPlayerCoreProps) {
  const tt = useTranslations("scriptPlayerModal")
  const td = useTranslations("contentDownload")
  // This modal deliberately keeps its own private engine instance rather than
  // the shared global one (see docs/KNOWN_ISSUES.md-adjacent audit -- the
  // preview/trial payment cutoff below requires exclusive control of the
  // engine, and this modal can be mounted more than once at a time). That
  // Restricted previews retain their configured instant-preview behavior;
  // complete script playback uses uploaded HSSP, like the curve editor.
  const handyPreviewPlaybackMode = useSettingsStore((state) => state.handyPreviewPlaybackMode)
  const useInstantPreview = (previewOnly || trialMode) && handyPreviewPlaybackMode === "instant"
  // Engine is created fresh each time the modal opens — eliminates stale callbacks.
  const engineRef = useRef<ScriptPlaybackEngine | null>(null)
  // True when user clicked "Keep playing" — cleanup must NOT stop the engine.
  const keepPlayingRef = useRef(false)
  // Guards onCutoffReached to fire once per trial run, not on every render while paused at cutoff.
  const firedCutoffRef = useRef(false)
  // Guards autoPlay to fire once per mount/open, not on every re-render.
  const autoPlayedRef = useRef(false)
  // True after handlePlay tells the engine to start, until the engine actually reaches
  // "playing" — video/audio playback waits for this so it never gets ahead of the Handy, which
  // can take real time (HSSP upload + clock sync + setup) to start moving on a cold play.
  const awaitingPlayStartRef = useRef(false)
  const isScrubbingRef = useRef(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const audioRef = useRef<HTMLAudioElement>(null)

  const [engineState, setEngineState] = useState<PlaybackState>("idle")
  const isPlaying = engineState === "playing"
  const isLoading = engineState === "loading"
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [muted, setMuted] = useState(false)
  // Trials always play silent — the video itself isn't the thing being sold, and letting a
  // public unpurchased preview carry audio is a licensing/content concern the app doesn't need.
  const effectiveMuted = trialMode ? true : muted
  const [mediaError, setMediaError] = useState(false)
  const [showDeviceSettings, setShowDeviceSettings] = useState(false)
  const [showQuickConnect, setShowQuickConnect] = useState(false)

  const { getActiveDevice, activeDeviceId, updateDeviceSettings } = useDeviceStore()
  const activeDevice = getActiveDevice()
  const strokeMin = activeDevice?.settings.minPosition ?? 0
  const strokeMax = activeDevice?.settings.maxPosition ?? 100

  // ── Engine lifecycle: create on open, destroy on close ───────────────────
  useEffect(() => {
    if (!open) return

    // Fresh engine every open — no stale closures
    const engine = new ScriptPlaybackEngine()
    engineRef.current = engine
    setEngineState("idle")
    setCurrentTime(0)
    setMediaError(false)
    firedCutoffRef.current = false
    autoPlayedRef.current = false
    awaitingPlayStartRef.current = false
    isScrubbingRef.current = false

    // Start prewarm in background so HSSP setup is done before user presses play
    if (script) engine.prewarm(script).catch(() => {})

    // Mirror status into the shared playback store while THIS modal is the one
    // actually driving the device -- previously this only happened after
    // "Keep playing" handed the engine to background-playback.ts, so up to
    // that point no other surface (the mini player, other tabs) had any way
    // to know a preview/trial/delivery-review was using the device at all.
    // Deliberately scoped to this effect's own open/close lifecycle rather
    // than a permanent registration -- does not touch background-playback.ts's
    // own separate, longer-lived handoff registration at all.
    const playbackStore = useScriptPlaybackStore.getState()
    let hasStartedInStore = false
    const scriptForStore = script

    const unsubState = engine.onStateChange((state) => {
      setEngineState(state)
      if (state === "playing") {
        if (!hasStartedInStore && scriptForStore) {
          hasStartedInStore = true
          playbackStore.startScript(scriptForStore.id, scriptForStore.name, false, totalDurationMsFn(scriptForStore) / 1000)
          playbackStore.registerControls({
            pause: () => engine.pause(),
            resume: () => engine.resume(),
            stop: () => engine.stop(),
          })
        } else if (hasStartedInStore) {
          playbackStore.resumeScript()
        }
      } else if (state === "paused" && hasStartedInStore) {
        playbackStore.pauseScript()
      }
    })
    const unsubTime = engine.onTimeUpdate((ms) => {
      if (!isScrubbingRef.current) setCurrentTime(ms / 1000)
      if (hasStartedInStore) playbackStore.updateTime(ms / 1000)
    })

    return () => {
      unsubState()
      unsubTime()
      // Stop the device unless the user pressed "Keep playing".
      // keepPlayingRef.current is checked here (not engineRef) because
      // handleKeepPlaying nulls engineRef but the closure still holds engine.
      if (!keepPlayingRef.current) {
        engine.stop()
        // Only clear the store if we're the ones who registered with it, and
        // only when NOT handing off to background -- startBackground() makes
        // its own fresh startScript() call moments before this cleanup runs,
        // and clearing here would immediately wipe that out.
        if (hasStartedInStore) playbackStore.stopScript()
      }
      keepPlayingRef.current = false
      engineRef.current = null
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (script) setDuration(totalDurationMsFn(script) / 1000)
  }, [script])

  // ── Helpers ──────────────────────────────────────────────────────────────
  function totalDurationMsFn(s: UserScript) {
    const lastAt = s.funscript.actions.at(-1)?.at ?? 0
    const meta = s.funscript.metadata?.duration ?? 0
    const metaMs = meta > 0 && meta < 7200 ? meta * 1000 : meta
    return Math.max(lastAt, metaMs)
  }

  const totalDurationMs = script ? totalDurationMsFn(script) : 0
  const maxDuration = Math.max(duration, totalDurationMs / 1000, 1)

  // ── Preview cutoff: clamp seek/playback when reviewing a delivery before approval,
  // or when sampling a marketplace trial ──
  const cutoffSec = (previewOnly || trialMode) && previewCutoffMs ? Math.max(0, previewCutoffMs / 1000) : null
  const lockedFromPercent = cutoffSec != null ? Math.min(100, (cutoffSec / maxDuration) * 100) : 100

  // Pause the instant playback reaches the cutoff — checked on every engine/media time update.
  useEffect(() => {
    if (cutoffSec == null || !isPlaying) return
    if (currentTime < cutoffSec) return
    engineRef.current?.pause()
    if (videoRef.current) { videoRef.current.pause(); videoRef.current.currentTime = cutoffSec }
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = cutoffSec }
    setCurrentTime(cutoffSec)
    if (trialMode && !firedCutoffRef.current) {
      firedCutoffRef.current = true
      onCutoffReached?.()
    }
  }, [currentTime, cutoffSec, isPlaying, trialMode, onCutoffReached])

  // ── Derive content type ──────────────────────────────────────────────────
  const contentUrl = script?.contentUrl ?? ""
  const contentType = script?.contentType

  // Multi-page gallery items (doujin/album/manga) carried over from delivery.
  const resolvedMediaItems = useMemo(
    () => normalizeOrderMediaItems(script?.contentMediaItems || []),
    [script?.contentMediaItems],
  )

  type MediaKind = "video" | "image" | "audio" | "album" | "none"
  const mediaKind: MediaKind = (() => {
    if (!script) return "none"
    // UserScript.contentType is collapsed to "image" for doujin/album/manga at
    // delivery time (see DELIVERED_CONTENT_TYPE_MAP) — multi-item galleries are
    // the only reliable signal left for "album" by the time content reaches the player.
    if (resolvedMediaItems.length > 1) return "album"
    if (contentType === "video" || contentType === "animation") return "video"
    if (contentType === "image") return "image"
    if (contentType === "audio" || script.audioSourceId) return "audio"
    if (contentUrl && isVideoUrl(contentUrl)) return "video"
    if (contentUrl && isImageUrl(contentUrl)) return "image"
    return "none"
  })()

  const isAlbum = mediaKind === "album"

  // ── Gallery page navigation — auto-advances with playback so a doujin/album plays through
  // its pages in sync with the script, same as Story Scripter's own player. Uses the real
  // per-page durations from Story Scripter's saved state when available (script was authored
  // there); otherwise falls back to splitting the total duration evenly across pages, since
  // there's no other timing signal for a flat multi-image gallery script. ──────────────────
  const [currentPageIndex, setCurrentPageIndex] = useState(0)
  const [userTookOverPaging, setUserTookOverPaging] = useState(false)
  useEffect(() => { setCurrentPageIndex(0); setUserTookOverPaging(false) }, [script?.id])
  useEffect(() => { setMediaError(false) }, [currentPageIndex])

  // ── Book script (Story Scriptor) page navigation ──────────────────────────
  const isBookScript = !!(script?.isBookScript && script?.pages && script.pages.length > 0)
  const bookPages = script?.pages || []
  const [bookPageIndex, setBookPageIndex] = useState(0)
  useEffect(() => { setBookPageIndex(0) }, [script?.id])

  // Autoplay / continuous mode for book scripts
  const [bookAutoPlay, setBookAutoPlay] = useState(false)
  const [bookEndBehavior, setBookEndBehavior] = useState<"stop" | "loop" | "repeat-last">("stop")
  const bookVideoRef = useRef<HTMLVideoElement>(null)

  // Per-page media info from the Story Scriptor editor state
  const editorPages = useMemo(() => {
    const state = script?.editorMetadata?.lastEditorState as { pages?: Array<{ fileUrl?: string; type?: string }> } | undefined
    return state?.pages ?? []
  }, [script?.id])

  // bookPageIndex now indexes into editorPages (ALL pages, scripted + unscripted).
  // When editorPages is unavailable we fall back to indexing bookPages directly.
  const hasEditorPages = editorPages.length > 0
  const totalBookPages = hasEditorPages ? editorPages.length : bookPages.length
  const currentEditorPage = hasEditorPages ? editorPages[bookPageIndex] : undefined
  // PageScript for the current editor page (null = unscripted page)
  const currentPageScript = hasEditorPages
    ? bookPages.find(p => p.pageIndex === bookPageIndex) ?? null
    : bookPages[bookPageIndex] ?? null
  const isCurrentBookPageVideo = currentEditorPage?.type === "video"
  const currentBookPageMediaUrl = currentEditorPage?.fileUrl || currentPageScript?.thumbnailUrl || contentUrl || ""

  const storyScripterPageDurationsMs = useMemo(() => {
    const pageScripts = (script?.editorMetadata?.lastEditorState as { pageScripts?: Record<number, { duration?: number }> } | undefined)?.pageScripts
    if (!pageScripts || resolvedMediaItems.length === 0) return null
    const durations = Array.from({ length: resolvedMediaItems.length }, (_, i) => (pageScripts[i]?.duration ?? 0) * 1000)
    return durations.every((d) => d > 0) ? durations : null
  }, [script, resolvedMediaItems.length])

  useEffect(() => {
    if (!isAlbum || resolvedMediaItems.length <= 1 || userTookOverPaging) return
    const currentMs = currentTime * 1000
    const pageDurationsMs = storyScripterPageDurationsMs
      ?? Array(resolvedMediaItems.length).fill(totalDurationMs / resolvedMediaItems.length)
    let cumulative = 0
    let targetPage = 0
    for (let i = 0; i < pageDurationsMs.length; i++) {
      if (currentMs >= cumulative) targetPage = i
      cumulative += pageDurationsMs[i]
    }
    if (targetPage !== currentPageIndex) setCurrentPageIndex(targetPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTime, isAlbum, resolvedMediaItems.length, storyScripterPageDurationsMs])

  // Non-autoplay: sync bookPageIndex with the haptic engine's playback position.
  useEffect(() => {
    if (!isBookScript || bookPages.length <= 1 || bookAutoPlay) return
    const currentMs = currentTime * 1000
    let targetScriptIdx = 0
    for (let i = 0; i < bookPages.length; i++) {
      if (currentMs >= bookPages[i].cumulativeTiming) targetScriptIdx = i
      else break
    }
    const targetEditorIdx = hasEditorPages
      ? (bookPages[targetScriptIdx]?.pageIndex ?? targetScriptIdx)
      : targetScriptIdx
    if (targetEditorIdx !== bookPageIndex) setBookPageIndex(targetEditorIdx)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTime, isBookScript, bookPages.length, bookAutoPlay, hasEditorPages])

  // Autoplay + scripted image page: advance when haptic engine passes end of this page's script.
  useEffect(() => {
    if (!isBookScript || !bookAutoPlay || isCurrentBookPageVideo || !currentPageScript) return
    if (totalBookPages <= 1) return
    const pageEndMs = currentPageScript.cumulativeTiming + currentPageScript.duration * 1000
    if (currentTime * 1000 < pageEndMs) return
    const nextIdx = bookPageIndex + 1
    if (nextIdx >= totalBookPages) {
      if (bookEndBehavior === "loop") goToBookPage(0)
      else if (bookEndBehavior === "repeat-last") goToBookPage(bookPageIndex)
    } else {
      goToBookPage(nextIdx)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTime, isBookScript, bookAutoPlay, isCurrentBookPageVideo, currentPageScript, bookPageIndex, bookEndBehavior, totalBookPages])

  // Autoplay + unscripted image page: show for 3 seconds then advance.
  useEffect(() => {
    if (!isBookScript || !bookAutoPlay || isCurrentBookPageVideo || currentPageScript) return
    if (totalBookPages <= 1) return
    const timer = setTimeout(() => {
      const nextIdx = bookPageIndex + 1
      if (nextIdx >= totalBookPages) {
        if (bookEndBehavior === "loop") goToBookPage(0)
        else if (bookEndBehavior === "repeat-last") goToBookPage(bookPageIndex)
      } else {
        goToBookPage(nextIdx)
      }
    }, 3000)
    return () => clearTimeout(timer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookPageIndex, isBookScript, bookAutoPlay, isCurrentBookPageVideo, currentPageScript, bookEndBehavior, totalBookPages])

  const currentPage = isAlbum
    ? resolvedMediaItems[Math.min(currentPageIndex, Math.max(0, resolvedMediaItems.length - 1))]
    : null
  const activeUrl = isAlbum ? (currentPage?.url || contentUrl) : contentUrl
  const activeType = isAlbum ? inferOrderMediaType(activeUrl, currentPage?.type) : mediaKind
  const playableUrl = activeUrl ? getImageUrl(activeUrl, { site: script?.contentSite || undefined }) : ""
  const hasMultiplePages = isAlbum && resolvedMediaItems.length > 1

  // Manual paging opts the user out of playback-synced auto-advance until they stop/restart —
  // otherwise the very next time update would just snap them back to the playback-derived page.
  const goToPreviousPage = () => { setUserTookOverPaging(true); setCurrentPageIndex((i) => Math.max(0, i - 1)) }
  const goToNextPage = () => { setUserTookOverPaging(true); setCurrentPageIndex((i) => Math.min(resolvedMediaItems.length - 1, i + 1)) }

  // Book script page navigation: accepts an editor page index (all pages).
  // Seeks the haptic engine to the matching PageScript's cumulativeTiming, if any.
  const goToBookPage = useCallback((editorPageIdx: number) => {
    const clampedIdx = Math.min(Math.max(0, editorPageIdx), totalBookPages - 1)
    setBookPageIndex(clampedIdx)
    const pageScript = hasEditorPages
      ? bookPages.find(p => p.pageIndex === clampedIdx)
      : bookPages[clampedIdx]
    if (!pageScript) return // unscripted page — don't move the haptic engine
    const seekMs = pageScript.cumulativeTiming
    const seekSec = seekMs / 1000
    setCurrentTime(seekSec)
    engineRef.current?.seek(seekMs)
    if (videoRef.current) videoRef.current.currentTime = seekSec
    if (audioRef.current) audioRef.current.currentTime = seekSec
  }, [bookPages, totalBookPages, hasEditorPages])

  // Called when a book-script video page ends — triggers autoplay advance
  const handleBookVideoEnded = useCallback(() => {
    if (!bookAutoPlay) return
    const nextIdx = bookPageIndex + 1
    if (nextIdx >= totalBookPages) {
      if (bookEndBehavior === "loop") goToBookPage(0)
      else if (bookEndBehavior === "repeat-last") goToBookPage(bookPageIndex)
      // "stop" — stay on last page
    } else {
      goToBookPage(nextIdx)
    }
  }, [bookAutoPlay, bookPageIndex, totalBookPages, bookEndBehavior, goToBookPage])

  const isMediaKindVisual = mediaKind === "video" || mediaKind === "image" || mediaKind === "album"
  const hasVisualContent = isAlbum ? !!activeUrl : !!contentUrl

  // ── Playback controls ────────────────────────────────────────────────────
  const handlePlay = useCallback(() => {
    if (!script || !engineRef.current) return
    // Allow clicking to cancel mid-setup
    if (isLoading) { engineRef.current.stop(); return }
    const engine = engineRef.current

    // At the preview cutoff — restart from the beginning rather than playing nothing.
    const startAt = cutoffSec != null && currentTime >= cutoffSec ? 0 : currentTime
    if (startAt === 0 && currentTime >= (cutoffSec ?? Infinity)) firedCutoffRef.current = false

    if (isPlaying) {
      engine.pause()
      videoRef.current?.pause()
      audioRef.current?.pause()
      bookVideoRef.current?.pause()
    } else if (engineState === "paused" && startAt === currentTime) {
      // Video/audio wait for the "playing" state effect below rather than starting here —
      // resume() still has to round-trip a playHSSP call to the Handy before it's actually
      // moving again, so starting the video eagerly would run it ahead of the device.
      awaitingPlayStartRef.current = true
      engine.resume()
    } else {
      if (startAt !== currentTime) setCurrentTime(startAt)
      if (videoRef.current) videoRef.current.currentTime = startAt
      if (audioRef.current) audioRef.current.currentTime = startAt
      if (bookVideoRef.current) bookVideoRef.current.currentTime = 0
      // Video/audio wait for the "playing" state effect below — a cold start can genuinely take
      // real time (HSSP upload + clock sync + device setup) before the Handy is actually moving,
      // and starting the video immediately here is exactly what put it seconds ahead of the device.
      awaitingPlayStartRef.current = true
      engine.play(script, {
        startTime: startAt * 1000,
        syncOffset: useGlobalPlaybackStore.getState().syncDelay,
        skipHSSP: useInstantPreview,
      }).catch((error) => {
        if (!isNoDeviceError(error)) console.error(error)
        // Engine never made it to "playing" (and never will for this attempt) — start the
        // video/audio anyway rather than leaving them waiting forever.
        if (awaitingPlayStartRef.current) {
          awaitingPlayStartRef.current = false
          videoRef.current?.play().catch(() => setMediaError(true))
          audioRef.current?.play().catch(() => setMediaError(true))
          bookVideoRef.current?.play().catch(() => {})
        }
      })
    }
  }, [script, isPlaying, isLoading, engineState, currentTime, cutoffSec, useInstantPreview])

  // Start video/audio the instant the engine actually reaches "playing" — not eagerly on click —
  // so playback never runs ahead of (or behind) whatever the Handy is really doing.
  useEffect(() => {
    if (engineState !== "playing" || !awaitingPlayStartRef.current) return
    awaitingPlayStartRef.current = false
    videoRef.current?.play().catch(() => setMediaError(true))
    audioRef.current?.play().catch(() => setMediaError(true))
    bookVideoRef.current?.play().catch(() => {})
  }, [engineState])

  // Auto-start playback once mounted — the host already captured the "play" intent itself
  // (e.g. marketplace "Try" button, clicked before this component's content even loaded).
  useEffect(() => {
    if (!open || !autoPlay || !script) return
    if (autoPlayedRef.current) return
    autoPlayedRef.current = true
    handlePlay()
  }, [open, autoPlay, script, handlePlay])

  // External pause/resume — lets a parent component (e.g. the marketplace Try button) control
  // playback without needing to re-mount the player.
  const prevExternalPausedRef = useRef(externalPaused)
  useEffect(() => {
    const prev = prevExternalPausedRef.current
    prevExternalPausedRef.current = externalPaused
    if (externalPaused === prev) return
    if (externalPaused) {
      if (isPlaying) {
        engineRef.current?.pause()
        videoRef.current?.pause()
        audioRef.current?.pause()
        bookVideoRef.current?.pause()
      }
    } else {
      if (engineState === "paused") {
        awaitingPlayStartRef.current = true
        engineRef.current?.resume()
      }
    }
  }, [externalPaused, isPlaying, engineState])

  const handleStop = useCallback(() => {
    isScrubbingRef.current = false
    engineRef.current?.stop()
    if (videoRef.current) { videoRef.current.pause(); videoRef.current.currentTime = 0 }
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0 }
    if (bookVideoRef.current) { bookVideoRef.current.pause(); bookVideoRef.current.currentTime = 0 }
    setCurrentTime(0)
    setEngineState("idle")
    setUserTookOverPaging(false)
  }, [])

  // While dragging the seek bar: only move the displayed time/heatmap needle — not the actual
  // video frame, haptic engine position, or (most importantly) the connected device, which would
  // otherwise get a real seek command + browser decode-seek on every single drag tick.
  const handleSeekPreview = useCallback((val: number[]) => {
    isScrubbingRef.current = true
    setUserTookOverPaging(false)
    const t = cutoffSec != null ? Math.min(val[0], cutoffSec) : val[0]
    setCurrentTime(t)
  }, [cutoffSec])

  // On release: commit the actual seek — this is the only place video/engine/device positions
  // need to move, so a fast scrub across the whole bar costs one seek, not dozens.
  const handleSeekCommit = useCallback((val: number[]) => {
    isScrubbingRef.current = false
    const t = cutoffSec != null ? Math.min(val[0], cutoffSec) : val[0]
    setCurrentTime(t)
    engineRef.current?.seek(t * 1000)
    if (videoRef.current) videoRef.current.currentTime = t
    if (audioRef.current) audioRef.current.currentTime = t
  }, [cutoffSec])

  const handleMediaTimeUpdate = () => {
    const el = videoRef.current ?? audioRef.current
    if (el && !isScrubbingRef.current) setCurrentTime(el.currentTime)
  }

  const handleMediaEnded = () => {
    engineRef.current?.stop()
    setCurrentTime(0)
  }

  // ── Keep playing in background ───────────────────────────────────────────
  const handleKeepPlaying = useCallback(() => {
    if (!script || !engineRef.current || !isPlaying) return
    // Signal cleanup NOT to stop the engine, then hand it off.
    keepPlayingRef.current = true
    startBackground(engineRef.current, script, totalDurationMs)
    onClose()
  }, [script, isPlaying, totalDurationMs, onClose])

  // ── Content download ──────────────────────────────────────────────────────
  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadProgress, setDownloadProgress] = useState(0)

  // One-time warning shown the first time any script is opened. Account-scoped
  // (see lib/client/seen-state.ts) so it doesn't reappear on a different
  // browser/device; logged-out visitors fall back to the old localStorage key.
  const { user: dlNoteUser, isLoading: dlNoteAuthLoading } = useAuthStore()
  useEffect(() => {
    if (!open || !script) return
    if (typeof window === "undefined") return
    if (dlNoteAuthLoading) return
    const KEY = "notice:script_download"
    const LOCAL_KEY = "goonru_dl_note_v1"
    let cancelled = false
    const run = async () => {
      let seen: boolean
      if (dlNoteUser) {
        const { keys } = await fetchSeenState()
        seen = keys.has(KEY)
        if (!seen) void markSeen(KEY)
      } else {
        seen = !!localStorage.getItem(LOCAL_KEY)
        if (!seen) localStorage.setItem(LOCAL_KEY, "1")
      }
      if (cancelled || seen) return
      setTimeout(() => {
        if (cancelled) return
        toast.info(tt("keepYourContentSafe"), {
          description: tt("contentCanBeRemovedFromItsSourceAtAnyTi"),
          duration: 9000,
        })
      }, 1400)
    }
    void run()
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const hasDownloadableContent = !previewOnly && !trialMode && (
    (isBookScript && editorPages.some(p => !!p.fileUrl)) ||
    (isAlbum && resolvedMediaItems.some(m => !!m.url)) ||
    !!contentUrl
  )

  const handleDownload = useCallback(async () => {
    if (!script || isDownloading) return

    const safeName = script.name.replace(/[^a-z0-9_\-. ]/gi, "_").trim() || "content"

    let items: DownloadItem[] = []

    if (isBookScript && editorPages.length > 0) {
      items = editorPages
        .filter(p => !!p.fileUrl)
        .map((p, i) => ({ url: p.fileUrl!, filename: `${String(i + 1).padStart(3, "0")}.${extFromUrl(p.fileUrl!)}` }))
    } else if (isAlbum && resolvedMediaItems.length > 0) {
      items = resolvedMediaItems
        .filter(m => !!m.url)
        .map((m, i) => ({ url: m.url, filename: `${String(i + 1).padStart(3, "0")}.${extFromUrl(m.url)}` }))
    } else if (contentUrl) {
      items = [{ url: contentUrl, filename: `${safeName}.${extFromUrl(contentUrl)}` }]
    }

    setIsDownloading(true)
    setDownloadProgress(0)
    try {
      await downloadContentItems(items, safeName, { onProgress: setDownloadProgress, t: td })
    } finally {
      setIsDownloading(false)
      setDownloadProgress(0)
    }
  }, [script, isDownloading, isBookScript, editorPages, isAlbum, resolvedMediaItems, contentUrl])

  if (!script) return null

  return (
    <>
      {showHeader && (
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border/60 shrink-0">
          {/* Title + meta — grows to fill available space */}
          <div className="min-w-0 flex-1">
            <DialogTitle className="text-sm font-semibold leading-tight truncate">
              {script.name}
            </DialogTitle>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-[11px] h-5 px-1.5 gap-1 font-normal">
                {mediaKind === "video" && <><Monitor className="h-3 w-3" />{tt("video")}</>}
                {mediaKind === "image" && <><FileText className="h-3 w-3" />{tt("image")}</>}
                {mediaKind === "audio" && <><Music className="h-3 w-3" />{tt("audio")}</>}
                {mediaKind === "album" && <><LayoutGrid className="h-3 w-3" />{tt("gallery")}</>}
                {mediaKind === "none" && <><FileText className="h-3 w-3" />{tt("scriptOnly")}</>}
              </Badge>
              {previewOnly && (
                <Badge className="text-[11px] h-5 px-1.5 font-normal bg-amber-500/15 text-amber-500 border-amber-500/30">
                  {tt("preview")}
                </Badge>
              )}
              {trialMode && (
                <Badge className="text-[11px] h-5 px-1.5 font-normal bg-amber-500/15 text-amber-500 border-amber-500/30">
                  {cutoffSec != null ? tt("trialCutoff", { time: formatTime(cutoffSec) }) : tt("trial")}
                </Badge>
              )}
              <span className="text-[11px] text-muted-foreground">
                {tt("countActions", { count: script.funscript.actions.length.toLocaleString() })}
              </span>
            </div>
          </div>

          {/* "Keep playing" — only shown while playing, and never during a preview/trial:
              handing an unpurchased/unapproved script off to the background singleton would let
              it keep silently driving the Handy past the intended cutoff. */}
          {isPlaying && !previewOnly && !trialMode && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1.5 text-xs shrink-0"
              onClick={handleKeepPlaying}
            >
              <Minimize2 className="h-3.5 w-3.5" />
              {tt("keepPlaying")}
            </Button>
          )}

          {/* Edit in Curve Editor */}
          {onEdit && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1.5 text-xs shrink-0 text-muted-foreground hover:text-foreground"
              onClick={() => { onEdit(script.id); onClose() }}
              title={tt("openInCurveEditor")}
            >
              <Edit2 className="h-3.5 w-3.5" />
              {tt("edit")}
            </Button>
          )}

          {/* Download content */}
          {hasDownloadableContent && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground relative"
              onClick={handleDownload}
              disabled={isDownloading}
              title={
                isBookScript || isAlbum
                  ? isDownloading ? `Zipping… ${downloadProgress}%` : "Download all pages as ZIP"
                  : isDownloading ? tt("downloading") : tt("downloadContent")
              }
              aria-label={tt("downloadContent")}
            >
              {isDownloading
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Download className="h-3.5 w-3.5" />}
              {isDownloading && (
                <span
                  className="absolute bottom-0 left-0 h-[2px] bg-primary rounded-full transition-all"
                  style={{ width: `${downloadProgress}%` }}
                />
              )}
            </Button>
          )}

          {/* Close button */}
          <DialogClose asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
              aria-label={tt("close")}
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogClose>
        </div>
      )}

        {/* Media area */}
        <div className="relative bg-black min-h-[280px] flex items-center justify-center overflow-hidden">

          {(previewOnly || trialMode) && <PreviewWatermark />}

          {/* Book script (Story Scriptor) page display */}
          {isBookScript && !mediaError && (
            <div className="relative flex items-center justify-center w-full">
              {isCurrentBookPageVideo ? (
                <video
                  key={`book-video-${bookPageIndex}`}
                  ref={bookVideoRef}
                  src={currentBookPageMediaUrl}
                  className={cn("max-h-[420px] w-full object-contain", previewOnly && "blur-[7px] scale-110")}
                  onEnded={handleBookVideoEnded}
                  onError={() => setMediaError(true)}
                  muted={effectiveMuted}
                  autoPlay={isPlaying}
                  playsInline
                />
              ) : (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  key={`book-img-${bookPageIndex}`}
                  src={getImageUrl(
                    currentPageScript?.thumbnailUrl || currentEditorPage?.fileUrl || contentUrl || "",
                    { site: script?.contentSite || undefined }
                  )}
                  alt={`${script.name} — page ${bookPageIndex + 1}`}
                  className={cn("max-h-[420px] max-w-full object-contain", previewOnly && "blur-[7px] scale-110")}
                  onError={() => setMediaError(true)}
                />
              )}
              {totalBookPages > 1 && (
                <div className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-medium text-white">
                  <LayoutGrid className="h-3 w-3" />
                  {bookPageIndex + 1} / {totalBookPages}
                </div>
              )}
            </div>
          )}

          {!isBookScript && mediaKind === "video" && !mediaError && contentUrl && (
            <video
              ref={videoRef}
              src={playableUrl}
              className={cn("max-h-[420px] w-full object-contain", previewOnly && "blur-[7px] scale-110")}
              onTimeUpdate={handleMediaTimeUpdate}
              onLoadedMetadata={(e) => setDuration((e.target as HTMLVideoElement).duration)}
              onEnded={handleMediaEnded}
              onError={() => setMediaError(true)}
              muted={effectiveMuted}
            />
          )}

          {!isBookScript && mediaKind === "image" && !mediaError && contentUrl && (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={playableUrl}
              alt={script.name}
              className={cn("max-h-[420px] max-w-full object-contain", previewOnly && "blur-[7px] scale-110")}
              onError={() => setMediaError(true)}
            />
          )}

          {/* Gallery page — independent of the haptic engine's video/audio refs,
              same as a static image: the engine keeps driving the device off
              the funscript timeline regardless of which page is shown. */}
          {!isBookScript && mediaKind === "album" && !mediaError && activeUrl && (
            <div className="relative flex items-center justify-center w-full">
              {activeType === "video" ? (
                // No native `controls` here — it used to rely on the browser's own transport,
                // but that's not wired to videoRef at all, and in preview mode the blur filter
                // (applied to this same element) blurred the native controls right along with the
                // video, making them unreadable. Use the same custom transport as the single-
                // video case below instead, which sits outside the blurred element entirely.
                <video
                  key={activeUrl}
                  ref={videoRef}
                  src={playableUrl}
                  preload="metadata"
                  className={cn("max-h-[420px] w-full object-contain", previewOnly && "blur-[7px] scale-110")}
                  onTimeUpdate={handleMediaTimeUpdate}
                  onLoadedMetadata={(e) => setDuration((e.target as HTMLVideoElement).duration)}
                  onEnded={handleMediaEnded}
                  onError={() => setMediaError(true)}
                  muted={effectiveMuted}
                />
              ) : activeType === "audio" ? (
                <div className="flex min-h-40 flex-col items-center justify-center gap-4 p-6">
                  <Music className="h-12 w-12 text-muted-foreground" />
                  <audio key={activeUrl} src={playableUrl} controls className="w-full max-w-xl" onError={() => setMediaError(true)} />
                </div>
              ) : (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={playableUrl}
                  alt={`${script.name} (${currentPageIndex + 1}/${resolvedMediaItems.length})`}
                  className={cn("max-h-[420px] max-w-full object-contain", previewOnly && "blur-[7px] scale-110")}
                  onError={() => setMediaError(true)}
                />
              )}
              {hasMultiplePages && (
                <div className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-medium text-white">
                  <LayoutGrid className="h-3 w-3" />
                  {currentPageIndex + 1} / {resolvedMediaItems.length}
                </div>
              )}
            </div>
          )}

          {!isBookScript && mediaKind === "audio" && (
            <>
              <audio
                ref={audioRef}
                src={playableUrl || undefined}
                onTimeUpdate={handleMediaTimeUpdate}
                onLoadedMetadata={(e) => setDuration((e.target as HTMLAudioElement).duration)}
                onEnded={handleMediaEnded}
              />
              <div className="w-full px-6 py-8">
                <div className="flex flex-col items-center gap-4">
                  <div className="h-24 w-24 rounded-full bg-purple-500/20 border border-purple-500/30 flex items-center justify-center">
                    <Music className={cn("h-10 w-10 text-purple-400", isPlaying && "animate-pulse")} />
                  </div>
                  {script.audioSourceTitle && (
                    <p className="text-sm text-muted-foreground">{script.audioSourceTitle}</p>
                  )}
                  <div className="w-full h-16 rounded-lg overflow-hidden">
                    <HeatmapCanvas script={script} currentTime={currentTime} />
                  </div>
                </div>
              </div>
            </>
          )}

          {!isBookScript && (mediaKind === "none" || mediaError || (isMediaKindVisual && !hasVisualContent)) && (
            <div className="w-full px-6 py-8">
              <div className="flex flex-col items-center gap-4 w-full">
                {mediaError && (activeUrl || contentUrl) && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                    <span>{tt("mediaUnavailable")}</span>
                    <a href={activeUrl || contentUrl} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 text-purple-400 hover:underline">
                      <ExternalLink className="h-3 w-3" />{tt("openExternally")}
                    </a>
                  </div>
                )}
                {isMediaKindVisual && !hasVisualContent && !mediaError && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                    <span>{tt("noContentAttachedToThisScript")}</span>
                  </div>
                )}
                <div className="w-full h-[120px] rounded-lg overflow-hidden border border-border/40">
                  <HeatmapCanvas script={script} currentTime={currentTime} />
                </div>
                <p className="text-xs text-muted-foreground">
                  {tt("countActionsDurationTotal", { count: script.funscript.actions.length, duration: formatTime(totalDurationMs / 1000) })}
                </p>
              </div>
            </div>
          )}

          {/* Album pages have their own native video/audio controls (independent of the
              haptic engine) — skip the big overlay so it doesn't block them; the
              bottom transport bar still drives the haptic engine in album mode. */}
          {((isMediaKindVisual && !isAlbum && !mediaError && !!contentUrl) || (isBookScript && !mediaError)) && !isPlaying && (
            <button
              type="button"
              aria-label={isLoading ? tt("settingUpDevice2") : tt("play")}
              onClick={handlePlay}
              disabled={isLoading}
              className="absolute inset-0 z-20 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors group"
            >
              <div className="h-14 w-14 rounded-full bg-background/90 flex items-center justify-center group-hover:scale-105 transition-transform shadow-xl">
                {isLoading
                  ? <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  : <Play className="h-6 w-6 ml-0.5" />}
              </div>
            </button>
          )}
        </div>

        {/* Gallery page navigation (album/doujin) */}
        {hasMultiplePages && (
          <div className="relative z-20 flex items-center gap-2 border-t border-border/60 bg-background/90 px-2 py-2 shrink-0">
            <Button type="button" variant="secondary" size="icon" className="h-8 w-8 shrink-0" onClick={goToPreviousPage} disabled={currentPageIndex === 0}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0 flex-1 flex justify-center">
              <span className="text-xs font-medium">{currentPageIndex + 1} / {resolvedMediaItems.length}</span>
            </div>
            <Button type="button" variant="secondary" size="icon" className="h-8 w-8 shrink-0" onClick={goToNextPage} disabled={currentPageIndex >= resolvedMediaItems.length - 1}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Book script (Story Scriptor) page navigation + autoplay controls */}
        {isBookScript && totalBookPages > 1 && (
          <div className="relative z-20 flex items-center gap-1.5 border-t border-border/60 bg-background/90 px-2 py-1.5 shrink-0">
            <Button type="button" variant="secondary" size="icon" className="h-7 w-7 shrink-0"
              onClick={() => goToBookPage(bookPageIndex - 1)} disabled={bookPageIndex === 0}>
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <div className="min-w-0 flex-1 flex justify-center">
              <span className="text-xs font-medium">{tt("pageCurrentOfTotal", { current: bookPageIndex + 1, total: totalBookPages })}</span>
            </div>
            <Button type="button" variant="secondary" size="icon" className="h-7 w-7 shrink-0"
              onClick={() => goToBookPage(bookPageIndex + 1)} disabled={bookPageIndex >= totalBookPages - 1}>
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
            {/* Autoplay toggle */}
            <Button
              type="button"
              size="icon"
              variant={bookAutoPlay ? "default" : "secondary"}
              className="h-7 w-7 shrink-0"
              title={bookAutoPlay ? tt("autoplayOnClickToDisable") : tt("enableAutoplay")}
              onClick={() => setBookAutoPlay((v) => !v)}
            >
              <Play className="h-3 w-3" />
            </Button>
            {/* End behavior — cycles stop → loop → repeat-last */}
            {bookAutoPlay && (
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="h-7 w-7 shrink-0"
                title={bookEndBehavior === "stop" ? "At end: Stop" : bookEndBehavior === "loop" ? "At end: Loop from start" : "At end: Repeat last page"}
                onClick={() => setBookEndBehavior((b) => b === "stop" ? "loop" : b === "loop" ? "repeat-last" : "stop")}
              >
                {bookEndBehavior === "stop"
                  ? <StopCircle className="h-3 w-3" />
                  : bookEndBehavior === "loop"
                  ? <Repeat className="h-3 w-3" />
                  : <Repeat1 className="h-3 w-3" />}
              </Button>
            )}
          </div>
        )}

        {/* ── Controls bar ───────────────────────────────────────────────── */}
        <div className="relative z-20 border-t border-border/60 shrink-0">
          {/* Heatmap strip */}
          <div className="relative">
            <ScriptHeatmap
              actions={script.funscript.actions}
              currentTime={currentTime}
              className="h-2 rounded-none"
            />
            {cutoffSec != null && <LockedZoneOverlay lockedFromPercent={lockedFromPercent} />}
          </div>
          <div className="px-4 pt-2 pb-2 space-y-2">
          {/* Seek bar */}
          <div className="relative">
            <Slider
              aria-label={tt("scriptPlaybackPosition")}
              value={[currentTime]}
              min={0}
              max={maxDuration}
              step={0.1}
              onValueChange={handleSeekPreview}
              onValueCommit={handleSeekCommit}
              onPointerDownCapture={() => { isScrubbingRef.current = true }}
              onPointerUpCapture={() => { isScrubbingRef.current = false }}
              onPointerCancel={() => { isScrubbingRef.current = false }}
              onLostPointerCapture={() => { isScrubbingRef.current = false }}
              className="w-full min-h-6"
            />
            {cutoffSec != null && (
              <div
                aria-hidden="true"
                className="absolute top-1/2 -translate-y-1/2 right-0 h-1.5 rounded-full pointer-events-none bg-muted-foreground/25"
                style={{ left: `${lockedFromPercent}%` }}
              />
            )}
          </div>
          {cutoffSec != null && trialMode ? (
            <p className="text-[11px] text-muted-foreground -mt-1">
              {tt("trialEndsAtTimePurchaseToUnlock", { time: formatTime(cutoffSec) })}
            </p>
          ) : cutoffSec != null ? (
            <p className="text-[11px] text-muted-foreground -mt-1">
              {tt("lockedPastTimeApproveToUnlock", { time: formatTime(cutoffSec) })}
            </p>
          ) : previewOnly ? (
            <p className="text-[11px] text-muted-foreground -mt-1">
              {tt("fullPreviewWatermarkedApproveToUnlockA")}
            </p>
          ) : null}

          {/* Controls row */}
          <div className="flex items-center gap-2">
            {/* Left: transport buttons + time */}
            <div className="flex items-center gap-1">
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={handlePlay} aria-label={isPlaying ? tt("pause") : isLoading ? tt("cancel") : tt("play")}>
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              </Button>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={handleStop} aria-label={tt("stop")}>
                <Square className="h-3.5 w-3.5" />
              </Button>
              {!trialMode && (mediaKind === "video" || mediaKind === "audio") && (
                <Button
                  size="icon" variant="ghost" className="h-8 w-8"
                  onClick={() => { const n = !muted; setMuted(n); if (videoRef.current) videoRef.current.muted = n }}
                  aria-label={muted ? tt("unmute") : tt("mute")}
                >
                  {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                </Button>
              )}
              {trialMode && (mediaKind === "video" || mediaKind === "audio") && (
                <Button size="icon" variant="ghost" className="h-8 w-8" disabled aria-label={tt("mutedTrial")} title={tt("trialsPlaySilent")}>
                  <VolumeX className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* Time — centred in remaining space */}
            <span className="flex-1 text-center text-xs tabular-nums text-muted-foreground select-none">
              {isLoading
                ? <span className="flex items-center justify-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" />{tt("settingUpDevice")}</span>
                : <>{formatTime(currentTime)}<span className="opacity-40"> / {formatTime(maxDuration)}</span></>
              }
            </span>

            {/* Right: device indicator — compact dot during play, expands when paused */}
            <div className={cn(
              "flex items-center gap-1.5 transition-all duration-300",
              isPlaying ? "opacity-30" : "opacity-100"
            )}>
              {/* Dot-only indicator while playing */}
              <button
                type="button"
                disabled={!!activeDevice?.isConnected || isPlaying}
                onClick={() => setShowQuickConnect(true)}
                aria-label={activeDevice?.isConnected ? undefined : tt("connectADevice")}
                className={cn(
                  "flex items-center gap-1.5 overflow-hidden transition-all duration-300",
                  isPlaying
                    ? "w-4 px-0"
                    : "text-[11px] px-2 py-0.5 rounded-full border font-medium",
                  activeDevice?.isConnected
                    ? "bg-green-500/10 border-green-500/30 text-green-400 cursor-default"
                    : "bg-muted/60 border-border text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
                )}
              >
                <div className={cn("h-2 w-2 rounded-full shrink-0",
                  activeDevice?.isConnected ? "bg-green-400" : "bg-muted-foreground/50")} />
                {!isPlaying && (
                  <span className="truncate max-w-[100px] whitespace-nowrap">
                    {activeDevice?.isConnected ? (activeDevice.name ?? "Connected") : "No device"}
                  </span>
                )}
              </button>
              {activeDevice?.isConnected && !isPlaying && (
                <Button
                  size="icon"
                  variant={showDeviceSettings ? "secondary" : "ghost"}
                  className="h-6 w-6 shrink-0"
                  onClick={() => setShowDeviceSettings(s => !s)}
                  aria-label={tt("strokeSettings")}
                >
                  <Settings2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          {showDeviceSettings && activeDeviceId && (
            <div className="pt-2 border-t border-border/40 space-y-3">
              <p className="text-xs font-medium text-muted-foreground">{tt("strokeRange")}</p>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-16 shrink-0">{tt("startIn")}</span>
                  <Slider value={[strokeMin]} min={0} max={strokeMax - 5} step={1}
                    onValueChange={([v]) => updateDeviceSettings(activeDeviceId, { minPosition: v })}
                    className="flex-1" />
                  <span className="text-xs tabular-nums text-muted-foreground w-8 text-right">{strokeMin}%</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-16 shrink-0">{tt("endOut")}</span>
                  <Slider value={[strokeMax]} min={strokeMin + 5} max={100} step={1}
                    onValueChange={([v]) => updateDeviceSettings(activeDeviceId, { maxPosition: v })}
                    className="flex-1" />
                  <span className="text-xs tabular-nums text-muted-foreground w-8 text-right">{strokeMax}%</span>
                </div>
              </div>
            </div>
          )}
          </div>{/* end inner px-4 */}
        </div>

        {(script.tags.length > 0 || script.description) && (
          <div className="px-4 pb-3 flex flex-wrap gap-1 border-t border-border/40 pt-2 shrink-0">
            {script.description && (
              <p className="text-xs text-muted-foreground w-full mb-1">{script.description}</p>
            )}
            {script.tags.map((tag) => (
              <Badge key={tag} variant="secondary" className="text-xs h-4 px-1.5">{tag}</Badge>
            ))}
          </div>
        )}
      <QuickConnectDeviceModal open={showQuickConnect} onClose={() => setShowQuickConnect(false)} />
    </>
  )
}

// ─── Dialog wrapper — used everywhere except the marketplace's inline trial embed ──────────────

export function ScriptPlayerModal(props: ScriptPlayerModalProps) {
  if (!props.script) return null
  return (
    <Dialog open={props.open} onOpenChange={(v) => { if (!v) props.onClose() }}>
      <DialogContent
        showCloseButton={false}
        className="max-w-[calc(100%-10px)] sm:max-w-3xl w-full p-0 gap-0 overflow-hidden bg-background border-border/60"
      >
        <ScriptPlayerCore {...props} />
      </DialogContent>
    </Dialog>
  )
}
